import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Target, Handshake, Leaf, ArrowRight } from "lucide-react";

const ABOUT_IMG =
  "https://images.unsplash.com/photo-1694802180731-1ee2ea37692a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.1, ease: "easeOut" } }),
};

const values = [
  { icon: ShieldCheck, title: "Pålidelighed", text: "Vi holder, hvad vi lover, og leverer sikkert og til tiden hver gang." },
  { icon: Target, title: "Kvalitet", text: "Høj kvalitet og professionalisme i hvert eneste led af transporten." },
  { icon: Handshake, title: "Service", text: "Tæt dialog og fleksibilitet, så løsningen altid passer til kunden." },
  { icon: Leaf, title: "Effektivitet", text: "Effektiv planlægning og ruter, der sparer tid og ressourcer." },
];

const stats = [
  { value: "100%", label: "Fokus på punktlighed" },
  { value: "Hele DK", label: "Landsdækkende kørsel" },
  { value: "Erhverv", label: "Dedikeret til virksomheder" },
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* Split hero */}
      <section className="mx-auto grid max-w-7xl items-center gap-14 px-6 py-20 lg:grid-cols-2">
        <motion.div initial="hidden" animate="show" variants={fadeUp}>
          <span className="text-sm font-semibold uppercase tracking-widest text-brand">Om os</span>
          <h1 className="mt-3 font-heading text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
            Din pålidelige transportpartner i Danmark
          </h1>
          <p className="mt-6 text-base leading-relaxed text-slate-600 md:text-lg">
            SB Logistik er specialiseret i professionelle transportløsninger til
            danske virksomheder. Vi hjælper vores kunder med sikker, effektiv og
            punktlig transport i hele Danmark.
          </p>
          <p className="mt-4 text-base leading-relaxed text-slate-600 md:text-lg">
            For os er kvalitet og service altid i fokus. Vi tror på tæt dialog,
            fleksibilitet og pålidelighed – og vi arbejder hver dag for at være
            den transportpartner, du kan regne med.
          </p>
        </motion.div>

        <motion.div
          initial="hidden" animate="show" custom={1} variants={fadeUp}
          className="overflow-hidden rounded-sm border border-slate-200"
        >
          <img src={ABOUT_IMG} alt="Varevogn på vejen" className="h-full max-h-[480px] w-full object-cover" />
        </motion.div>
      </section>

      {/* Stats */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="mx-auto grid max-w-7xl gap-8 px-6 py-14 sm:grid-cols-3">
          {stats.map((s) => (
            <div key={s.label} data-testid={`about-stat-${s.label}`} className="text-center">
              <div className="font-heading text-3xl font-bold text-brand md:text-4xl">{s.value}</div>
              <div className="mt-2 text-sm font-medium text-slate-600">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Values */}
      <section className="mx-auto max-w-7xl px-6 py-24">
        <div className="max-w-2xl">
          <span className="text-sm font-semibold uppercase tracking-widest text-brand">Vores værdier</span>
          <h2 className="mt-3 font-heading text-3xl font-semibold tracking-tight text-slate-900 md:text-4xl">
            Det, vi står for
          </h2>
        </div>
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {values.map((v, i) => (
            <motion.div
              key={v.title}
              initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fadeUp}
              data-testid={`value-card-${i}`}
              className="rounded-sm border border-slate-200 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-slate-300"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-sm bg-brand/10 text-brand">
                <v.icon className="h-6 w-6" />
              </span>
              <h3 className="mt-5 font-heading text-lg font-semibold text-slate-900">{v.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{v.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-slate-900">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 px-6 py-16 md:flex-row md:items-center">
          <h2 className="max-w-xl font-heading text-2xl font-semibold tracking-tight text-white md:text-3xl">
            Lad os tage en snak om din transport
          </h2>
          <Link
            data-testid="about-cta-button"
            to="/kontakt"
            className="inline-flex shrink-0 items-center gap-2 rounded-sm bg-brand px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-brand-hover"
          >
            Kontakt os <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
